import camelCase from "lodash/camelCase";
import escape from "lodash/escape";
import get from "lodash/get";
import type {
  EmailMetaData,
  FileStorage,
  Location,
  TicketDetails,
} from "@/_store/tickets/tickets.module";
import { TicketsModal, TicketTrigger, TicketType, ViolationDirection } from "@/constants/tickets";
import { convertSize } from "@coronet/cui";
import { getFormattedDateTime } from "@/_helpers/date";
import { i18n } from "@/plugins/i18n";
import { countriesList } from "@/constants/countries";

/**
 * Builds the "key details" rows shown in the ticket preview panel.
 *
 * A `TicketKeyDetail` is a single-labelled row: `titleKey` is a rendered i18n
 * string and `value` is either plain text/HTML, a string list, or (for
 * `loopWithIcon` rows) an array of `TicketKeyDetailListItem`.
 *
 * `mapKeyDetails` dispatches to a per-scenario mapper in three ordered steps,
 * and the order encodes precedence:
 *   1. `ticketTypeMap` — matched by `eventType` (wins over any trigger).
 *   2. `endpointTriggerMap` — only for endpoint `eventType`s; the matched
 *      mapper supplies the middle rows, wrapped with shared model/OS + device rows.
 *   3. `generalTriggerMap` — matched by `eventTrigger`; unmatched → `[]`.
 *
 * Small `map*` helpers at the bottom (`mapEmailAttachments`, `mapEventTimes`,
 * `mapFileStorage`, `mapIps`, `mapModelAndOs`) hold rows shared across mappers.
 */

/**
 * A single item in a `loopWithIcon` row: `label` is the text to display and
 * `icon` an optional glyph. Mappers put whatever field they want shown into
 * `label`, so no per-item field name leaks into the type.
 */
export interface TicketKeyDetailListItem {
  label: string;
  icon?: string;
}

export interface TicketKeyDetail {
  value: string | string[] | TicketKeyDetailListItem[] | undefined;
  titleKey: string;
  ticketModal?: TicketsModal;
  loopWithIcon?: boolean;
  showItemsNumber?: number;
  tooltip?: string;
  chipList?: boolean;
}
type KeyDetailMapper = (ticket: TicketDetails) => TicketKeyDetail[];

/**
 * Ticket types dispatched by `eventType`. Checked before any `eventTrigger`
 * lookup, so an entry here takes precedence over the trigger maps below.
 * Types that resolve to the same handler get one entry each.
 */
const ticketTypeMap = new Map<TicketType, KeyDetailMapper>([
  [
    TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_EMAIL,
    mapSuspectedDataComplianceViolationByEmailDlp,
  ],
  [
    TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_EMAIL,
    mapSuspectedDataComplianceViolationByEmailDlp,
  ],
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_DEVICE, mapDeviceComplianceViolation],
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_DEVICE, mapDeviceComplianceViolation],
  [TicketType.SUSPECTED_DATA_EXPOSURE_BY_EMAIL, mapSuspectedExposureOfCriticalDataByEmail],
  [TicketType.MALICIOUS_URL_CLICK, mapMaliciousUrlClick],
  [TicketType.WIFI_FORBIDDEN_NETWORK, mapWifiForbiddenNetwork],
  [TicketType.BLOCKED_CONNECTION, mapBlockedDestinationConnection],
  [TicketType.FILE_INTEGRITY_VIOLATION, mapFileIntegrityViolation],
  [TicketType.SIMULATED_EMAIL_PHISHING, mapSimulatedEmailPhishing],
  [TicketType.SECURITY_TRAINING_FAILURE, mapSecurityTrainingFailure],
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_SHARING, mapExposureBySharing],
  [TicketType.SUSPECTED_DATA_EXPOSURE_BY_SHARING, mapExposureBySharing],
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_SHARING, mapExposureBySharing],
]);

/** Event types whose details are dispatched via `endpointTriggerMap`. */
const endpointEventTypes = new Set<TicketType>([
  TicketType.ENDPOINT_VULNERABILITY,
  TicketType.MALWARE_IN_DEVICE,
]);

/**
 * Endpoint triggers. Each mapper contributes only the trigger-specific rows;
 * `mapKeyDetails` wraps them with the shared model/OS and generic device rows,
 * so an unmatched trigger naturally yields just those shared rows.
 */
const endpointTriggerMap = new Map<TicketTrigger, KeyDetailMapper>([
  [TicketTrigger.MALWARE_ON_ENDPOINT, mapMalwareOnEndpoint],
  [TicketTrigger.INFECTED_PROCESS, mapInfectedProcess],
  [TicketTrigger.VSS_BACKUP_PROTECTION, mapVSSBackupProtection],
  [TicketTrigger.ENCRYPTION_DISABLED, mapUnencryptedDrive],
]);

/** Remaining triggers (cloud, identity, email, network). Shared handlers repeat per key. */
const generalTriggerMap = new Map<TicketTrigger, KeyDetailMapper>([
  [TicketTrigger.MALWARE_IN_CLOUD_DRIVE, mapMalwareInCloudDrive],
  [TicketTrigger.ACCESS_PERMISSIONS_VIOLATION, mapIpAddresses],
  [TicketTrigger.BLOCKED_IP_LOGIN, mapIpAddresses],
  [TicketTrigger.SUSPECTED_IDENTITY_COMPROMISE, mapIpAddresses],
  [TicketTrigger.ABNORMAL_ADMIN_ACTIVITY, mapIpAddresses],
  [TicketTrigger.IMPOSSIBLE_TRAVELER, mapEventHappenedTimes],
  [TicketTrigger.MALWARE_IN_EMAIL_ATTACHMENTS, mapMalwareInEmail],
  [TicketTrigger.EMAIL_PHISHING, mapEmailPhishing],
  [TicketTrigger.MISSING_REQUIRED_AUTHENTICATION, mapEmailPhishing],
  [TicketTrigger.BLOCKLISTED_SENDER, mapEmailPhishing],
  [TicketTrigger.CROWDBLOCKED_SENDER, mapEmailPhishing],
  [TicketTrigger.USER_IMPERSONATION, mapEmailPhishing],
  [TicketTrigger.DOMAIN_IMPERSONATION, mapEmailPhishing],
  [TicketTrigger.BRAND_IMPERSONATION, mapEmailPhishing],
  [TicketTrigger.PROMPT_INJECTION, mapEmailPhishing],
  [TicketTrigger.SUSPICIOUS_CONTENT, mapEmailPhishing],
  [TicketTrigger.SPAM, mapEmailPhishing],
  [TicketTrigger.FORBIDDEN_ATTACHMENT_TYPE, mapEmailPhishing],
  [TicketTrigger.DOMAIN_SPOOFING, mapEmailPhishing],
  [TicketTrigger.REPORTED_BY_USER, mapEmailPhishing],
  [TicketTrigger.SUSPICIOUS_METADATA, mapEmailPhishing],
  [TicketTrigger.WIFI_PHISHING, mapWifiPhishing],
  [TicketTrigger.ABNORMAL_DATA_MANIPULATION, mapAbnormalDataManipulation],
  [TicketTrigger.MASS_DOWNLOAD, mapAbnormalDataManipulation],
  [TicketTrigger.MASS_DELETE, mapAbnormalDataManipulation],
  [TicketTrigger.SUSPECTED_BOT_ATTACKS, mapSuspectedBotAttacks],
]);

/** Resolves a ticket to its ordered list of key-detail rows (see module doc). */
export const mapKeyDetails = (ticket: TicketDetails): TicketKeyDetail[] => {
  if (!ticket) return [];

  const typeMapper = ticketTypeMap.get(ticket.eventType);
  if (typeMapper) return typeMapper(ticket);

  if (endpointEventTypes.has(ticket.eventType)) {
    const triggerMapper = endpointTriggerMap.get(ticket.eventTrigger);
    return [
      ...mapModelAndOs(ticket),
      ...(triggerMapper ? triggerMapper(ticket) : []),
      ...mapDeviceGenericDetails(ticket),
    ];
  }

  return generalTriggerMap.get(ticket.eventTrigger)?.(ticket) ?? [];
};

/** Composite for the device compliance/sensitive-data types: model/OS + device DLP rows. */
function mapDeviceComplianceViolation(ticket: TicketDetails): TicketKeyDetail[] {
  return [...mapModelAndOs(ticket), ...mapDeviceDlp(ticket)];
}

/**
 * Email DLP compliance/sensitive-data violation rows.
 * The sender block depends on the violation direction: ACCESS shows full sender
 * info (name/email/ip) + recipients, while SHARE shows only the sender email +
 * recipients. Legacy tickets default to SHARE.
 */
function mapSuspectedDataComplianceViolationByEmailDlp(ticket: TicketDetails): TicketKeyDetail[] {
  const emailMetaData = ticket.sections.emailMetaData;
  // for old dlp events the default direction was SHARE
  const violationDirection = get(
    ticket,
    "sections.userDataAccessViolation.violationDirection",
    ViolationDirection.SHARE
  );
  const senderFullInfoAndRecipient = [
    {
      titleKey: i18n.global.t("ticketDetails.senderName"),
      value: escape(emailMetaData.senderName),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderEmail"),
      value: escape(emailMetaData.senderEmail),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderIp"),
      value: escape(emailMetaData.senderIp),
    },
    {
      titleKey: i18n.global.t("ticketDetails.recipients"),
      value: emailMetaData.recipients,
      ticketModal: TicketsModal.RECIPIENTS,
    },
  ];
  const senderShortInfoAndRecipient = [
    {
      titleKey: i18n.global.t("ticketDetails.sender"),
      value: escape(emailMetaData.senderEmail),
    },
    {
      titleKey: i18n.global.t("ticketDetails.recipients"),
      value: emailMetaData.recipients,
      ticketModal: TicketsModal.RECIPIENTS,
    },
  ];
  const senderAndRecipient =
    violationDirection === ViolationDirection.ACCESS
      ? senderFullInfoAndRecipient
      : senderShortInfoAndRecipient;

  return [
    ...senderAndRecipient,
    {
      titleKey: i18n.global.t("ticketDetails.subject"),
      value: escape(emailMetaData.subject),
    },
    ...mapEmailAttachments(emailMetaData),
    {
      titleKey: i18n.global.t("ticketDetails.internetMessageId"),
      value: escape(emailMetaData.internetMessageId),
    },
  ];
}

function mapMaliciousUrlClick(ticket: TicketDetails): TicketKeyDetail[] {
  const metadata = ticket.sections.maliciousUrlClickMetaData;
  return [
    {
      titleKey: i18n.global.t("ticketDetails.originalUrl"),
      value: metadata?.clickedUrl
        ? `<span class="url-chip">${escape(metadata.clickedUrl)}</span>`
        : undefined,
    },
    {
      titleKey: i18n.global.t("ticketDetails.workspaceId"),
      value: escape(ticket.workspaceId),
    },
    {
      titleKey: i18n.global.t("ticketDetails.recipientEmail"),
      value: escape(ticket.sections.emailMetaData?.recipients?.[0]),
      tooltip: i18n.global.t("ticketDetails.tooltips.recipientEmail"),
    },
    {
      titleKey: i18n.global.t("ticketDetails.clickCount"),
      value: metadata?.clickCount?.toString(),
      tooltip: i18n.global.t("ticketDetails.tooltips.clickCount"),
    },
    {
      titleKey: i18n.global.t("ticketDetails.timeOfTheFirstClick"),
      value: getFormattedDateTime(ticket.creationTime),
    },
  ];
}

/**
 * Critical-data exposure by email rows. Mirrors
 * {@link mapSuspectedDataComplianceViolationByEmailDlp} but differs on SHARE:
 * here SHARE shows recipients only (no sender), whereas ACCESS shows the full
 * sender block + recipients. Legacy tickets default to SHARE.
 */
function mapSuspectedExposureOfCriticalDataByEmail(ticket: TicketDetails): TicketKeyDetail[] {
  const emailMetaData = ticket.sections.emailMetaData;
  // for old dlp events the default direction was SHARE
  const violationDirection = get(
    ticket,
    "sections.userDataAccessViolation.violationDirection",
    ViolationDirection.SHARE
  );
  const senderAndRecipient = [
    {
      titleKey: i18n.global.t("ticketDetails.senderName"),
      value: escape(emailMetaData.senderName),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderEmail"),
      value: escape(emailMetaData.senderEmail),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderIp"),
      value: escape(emailMetaData.senderIp),
    },
    {
      titleKey: i18n.global.t("ticketDetails.recipients"),
      value: emailMetaData.recipients,
      ticketModal: TicketsModal.RECIPIENTS,
    },
  ];
  const recipient = [
    {
      titleKey: i18n.global.t("ticketDetails.recipients"),
      value: emailMetaData.recipients,
      ticketModal: TicketsModal.RECIPIENTS,
    },
  ];
  const senderOrRecipient =
    violationDirection === ViolationDirection.ACCESS ? senderAndRecipient : recipient;

  return [
    ...senderOrRecipient,
    {
      titleKey: i18n.global.t("ticketDetails.subject"),
      value: escape(emailMetaData.subject),
    },
    ...mapEmailAttachments(emailMetaData),
    {
      titleKey: i18n.global.t("ticketDetails.internetMessageId"),
      value: escape(emailMetaData.internetMessageId),
    },
  ];
}

function mapExposureBySharing(ticket: TicketDetails): TicketKeyDetail[] {
  const filesMetaData = ticket.sections.filesMetaData?.[0];
  if (!filesMetaData) return [];
  return [
    {
      titleKey: i18n.global.t("ticketDetails.sharedBy"),
      value: filesMetaData.sharedBy,
    },
    {
      titleKey: i18n.global.t("ticketDetails.sharedWith"),
      value: filesMetaData.sharedWith,
      ticketModal: TicketsModal.SHARED_WITH,
    },
    {
      titleKey: i18n.global.t("ticketDetails.sharedFolderPath"),
      value: filesMetaData?.sharedFolderPath,
    },
    {
      titleKey: i18n.global.t("ticketDetails.fileName"),
      value: filesMetaData?.fileName,
    },
    {
      titleKey: i18n.global.t("ticketDetails.fileSize"),
      value: filesMetaData?.fileSize ? convertSize(filesMetaData.fileSize, 1) : undefined,
    },
    {
      titleKey: i18n.global.t("ticketDetails.filePath"),
      value: filesMetaData?.filePath || filesMetaData?.path,
    },
    {
      titleKey: i18n.global.t("ticketDetails.owner"),
      value: filesMetaData?.owner,
    },
    {
      titleKey: i18n.global.t("ticketDetails.lastModifiedBy"),
      value: filesMetaData?.lastModifiedBy,
    },
    {
      titleKey: i18n.global.t("ticketDetails.lastModifiedTime"),
      value: filesMetaData.lastModifiedTime
        ? getFormattedDateTime(filesMetaData.lastModifiedTime, "ll | LT")
        : undefined,
    },
  ];
}

function mapIpAddresses(ticket: TicketDetails): TicketKeyDetail[] {
  return [mapIps(ticket.sections.locations), mapEventTimes(ticket.sections.counter)];
}

function mapEventHappenedTimes(ticket: TicketDetails): TicketKeyDetail[] {
  return [mapEventTimes(ticket.sections.counter)];
}

function mapMalwareOnEndpoint(ticket: TicketDetails): TicketKeyDetail[] {
  const malwareMetaData = ticket.sections.malwareMetaData;
  const keys = ["fileName", "path", "virusName", "containerHash", "fileHash"] as const;
  const values = keys.map((key) => ({
    titleKey: i18n.global.t(`ticketDetails.${key}`),
    value: malwareMetaData[key],
  }));
  return [
    {
      titleKey: i18n.global.t("ticketDetails.threatType"),
      value: malwareMetaData.threatType
        ? i18n.global.t(`ticketDetails.threatTypes.${camelCase(malwareMetaData.threatType)}`)
        : undefined,
    },
    ...values,
  ];
}

/**
 * Infected-process rows. The only mapper producing a `loopWithIcon` row: its
 * `value` is a list of objects the consumer renders by reading each item's own
 * `key` field. `key` is set to `"path"` so the consumer displays `group.path`,
 * and `icon` flags detected processes with a warning glyph.
 */
function mapInfectedProcess(ticket: TicketDetails): TicketKeyDetail[] {
  const processMetaData = ticket.sections.infectedProccessMetaData;

  return [
    {
      titleKey: i18n.global.t("ticketDetails.processGroup"),
      loopWithIcon: true,
      value: processMetaData.processGroups.map((group): TicketKeyDetailListItem => ({
        label: group.path,
        icon: group.detected ? "icon-warning-xs" : undefined,
      })),
    },
    {
      titleKey: i18n.global.t("ticketDetails.suspiciousCharacteristics"),
      value: processMetaData.suspiciousCharacteristics, // This can be a string
    },
    {
      titleKey: i18n.global.t("ticketDetails.suspectedMaliciousProcessName"),
      value: processMetaData.suspectedMaliciousProcessName, // This can be a string
    },
  ];
}

function mapVSSBackupProtection(ticket: TicketDetails): TicketKeyDetail[] {
  const processMetaData = ticket.sections.infectedProccessMetaData;
  return [
    {
      titleKey: i18n.global.t("ticketDetails.suspiciousCharacteristics"),
      value: processMetaData.suspiciousCharacteristics,
    },
    {
      titleKey: i18n.global.t("ticketDetails.suspectedMaliciousProcessName"),
      value: processMetaData.suspectedMaliciousProcessName,
    },
  ];
}

function mapUnencryptedDrive(ticket: TicketDetails): TicketKeyDetail[] {
  const unencryptedFileStorage = ticket.sections.deviceMetaData.unencryptedFileStorage;
  if (!unencryptedFileStorage) return [];
  return mapFileStorage(unencryptedFileStorage);
}

function mapDeviceDlp(ticket: TicketDetails): TicketKeyDetail[] {
  const scannedFileStorage = ticket.sections.deviceMetaData?.scannedFileStorage;
  if (!scannedFileStorage) return [];
  return [...mapFileStorage(scannedFileStorage), mapIps(ticket.sections.locations)];
}

function mapMalwareInCloudDrive(ticket: TicketDetails): TicketKeyDetail[] {
  const { triggeredUser, locations, malwareMetaData, cloudActivityLogsMetaData } = ticket.sections;
  const location = locations?.[0];
  const type = cloudActivityLogsMetaData?.[0].logType;
  const items: TicketKeyDetail[] = [
    {
      titleKey: i18n.global.t("ticketDetails.performedBy"),
      value: triggeredUser,
    },
  ];

  if (malwareMetaData?.sharedWith.length) {
    items.push({
      titleKey: i18n.global.t("ticketDetails.recipients"),
      value: malwareMetaData?.sharedWith,
      chipList: true,
    });
  }

  items.push(
    {
      titleKey: i18n.global.t("ticketDetails.ipAddress"),
      value: location?.ip,
    },
    {
      titleKey: i18n.global.t("ticketDetails.country"),
      value: location?.countryName,
    },
    {
      titleKey: i18n.global.t("ticketDetails.type"),
      value: i18n.global.t(`ticketDetails.fullDetails.anomalyDetection.${type}`),
    }
  );

  return items;
}

function mapMalwareInEmail(ticket: TicketDetails): TicketKeyDetail[] {
  const emailMetaData = ticket.sections.emailMetaData;
  const malwareMetaData = ticket.sections.malwareMetaData;

  return [
    {
      titleKey: i18n.global.t("ticketDetails.senderName"),
      value: escape(emailMetaData.senderName),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderEmail"),
      value: escape(emailMetaData.senderEmail),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderIp"),
      value: escape(emailMetaData.senderIp),
    },
    {
      titleKey: i18n.global.t("ticketDetails.subject"),
      value: escape(emailMetaData.subject),
    },
    {
      titleKey: i18n.global.t("ticketDetails.internetMessageId"),
      value: escape(emailMetaData.internetMessageId),
    },
    {
      titleKey: i18n.global.t("ticketDetails.virusName"),
      value: malwareMetaData?.virusName,
    },
  ];
}

/**
 * Shared handler for the email phishing family (phishing, impersonation, spam,
 * spoofing, etc.) — referenced by many entries in `generalTriggerMap`.
 * The sender-country row is only added when the backend resolved it.
 */
function mapEmailPhishing(ticket: TicketDetails): TicketKeyDetail[] {
  const emailMetaData = ticket.sections.emailMetaData;
  return [
    {
      titleKey: i18n.global.t("ticketDetails.subject"),
      value: escape(emailMetaData.subject),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderName"),
      value: escape(emailMetaData.senderName),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderEmail"),
      value: escape(emailMetaData.senderEmail),
    },
    {
      titleKey: i18n.global.t("ticketDetails.senderIp"),
      value: escape(emailMetaData.senderIp),
    },
    // only present when the backend resolved the sender's country
    ...(emailMetaData.senderCountry
      ? [
          {
            titleKey: i18n.global.t("ticketDetails.senderCountry"),
            value: escape(
              countriesList[emailMetaData.senderCountry] ?? emailMetaData.senderCountry
            ),
          },
        ]
      : []),
    ...(emailMetaData.senderTld
      ? [
          {
            titleKey: i18n.global.t("ticketDetails.senderTld"),
            value: escape(`.${emailMetaData.senderTld.replace(/^\./, "")}`),
          },
        ]
      : []),
    {
      titleKey: i18n.global.t("ticketDetails.internetMessageId"),
      value: escape(emailMetaData.internetMessageId),
    },
    mapIps(ticket.sections.locations),
  ];
}

function mapWifiPhishing(ticket: TicketDetails): TicketKeyDetail[] {
  const networkMetaData = ticket.sections.networkMetaData;
  return [
    {
      titleKey: i18n.global.t("ticketDetails.ssid"),
      value: networkMetaData.ssid,
    },
    {
      titleKey: i18n.global.t("ticketDetails.bssid"),
      value: networkMetaData.bssids,
      ticketModal: TicketsModal.BSSID_MODAL,
    },
    {
      titleKey: i18n.global.t("ticketDetails.security"),
      value: networkMetaData.networkSecurity,
    },
    {
      titleKey: i18n.global.t("ticketDetails.ticketDetails"),
      value: networkMetaData.indicators
        .map((explanation) => `<div>${i18n.global.t(`incidentsExplanations.${explanation}`)}</div>`)
        .join(""),
    },
  ];
}
function mapWifiForbiddenNetwork(ticket: TicketDetails): TicketKeyDetail[] {
  const networkMetaData = ticket.sections.networkMetaData;
  return [
    {
      titleKey: i18n.global.t("ticketDetails.ssid"),
      value: networkMetaData.ssid,
    },
    {
      titleKey: i18n.global.t("ticketDetails.security"),
      value: networkMetaData.networkSecurity,
    },
  ];
}

function mapSimulatedEmailPhishing(ticket: TicketDetails): TicketKeyDetail[] {
  const metadata = ticket.sections.securityAwarenessMetaData!;

  return [
    {
      titleKey: i18n.global.t("ticketDetails.campaignName"),
      value: metadata.campaignName,
    },
    {
      titleKey: i18n.global.t("ticketDetails.emailTemplate"),
      value: metadata.emailTemplate,
    },
    {
      titleKey: i18n.global.t("ticketDetails.emailSender"),
      value: metadata.emailSender,
    },
    {
      titleKey: i18n.global.t("ticketDetails.attachmentType"),
      value: metadata.attachmentType,
    },
  ];
}

function mapSecurityTrainingFailure(ticket: TicketDetails): TicketKeyDetail[] {
  const metadata = ticket.sections.securityAwarenessMetaData!;

  return [
    {
      titleKey: i18n.global.t("ticketDetails.campaignName"),
      value: metadata.campaignName,
    },
    {
      titleKey: i18n.global.t("ticketDetails.contents"),
      value: metadata.securityTrainingContents,
      showItemsNumber: 3,
      ticketModal: TicketsModal.CONTENTS,
    },
  ];
}

function mapAbnormalDataManipulation(ticket: TicketDetails): TicketKeyDetail[] {
  return [mapIps(ticket.sections.locations)];
}

function mapSuspectedBotAttacks(ticket: TicketDetails): TicketKeyDetail[] {
  return [
    mapIps(ticket.sections.locations),
    mapEventTimes(
      ticket.sections.counter,
      "ticketDetails.eventHappenedMessages.suspectedBotAttacks"
    ),
  ];
}

/** File/attachment rows shared by the email DLP mappers. */
function mapEmailAttachments(emailMetaData: EmailMetaData): TicketKeyDetail[] {
  if (emailMetaData.attachments?.length) {
    return [
      {
        titleKey: i18n.global.t("ticketDetails.attachments", emailMetaData.attachments.length),
        value: emailMetaData.attachments.map((item) => item.fileName),
        showItemsNumber: 1,
        ticketModal: TicketsModal.FILE_NAME,
      },
    ];
  }
  // Legacy tickets carry a single `attachment` instead of the `attachments` array.
  return [
    {
      titleKey: i18n.global.t("ticketDetails.fileName"),
      value: emailMetaData.attachment?.fileName,
    },
    {
      titleKey: i18n.global.t("ticketDetails.fileSize"),
      value: emailMetaData.attachment
        ? convertSize(emailMetaData.attachment.fileSize, 1)
        : undefined,
    },
  ];
}

/** "Happened N times" row; `titleKey` varies per scenario. */
function mapEventTimes(
  counter: number,
  titleKey = "ticketDetails.eventHappenedMessages.default"
): TicketKeyDetail {
  return {
    titleKey: i18n.global.t(titleKey),
    value: i18n.global.t("ticketDetails.times", { n: counter }, counter),
  };
}

/** Drive name/model/interface rows shared by the device storage mappers. */
function mapFileStorage(fileStorage: FileStorage): TicketKeyDetail[] {
  return [
    {
      titleKey: i18n.global.t("ticketDetails.driveName"),
      value: fileStorage.name,
    },
    {
      titleKey: i18n.global.t("ticketDetails.driveModel"),
      value: fileStorage.model,
    },
    {
      titleKey: i18n.global.t("ticketDetails.interfaceType"),
      value: fileStorage.interfaceType,
    },
  ];
}

/**
 * A single "IP and country" row. The value is an HTML fragment (rendered via
 * `v-html` in the consumer), so every interpolated field is `escape.
 */
function mapIps(ips: Location[]): TicketKeyDetail {
  return {
    titleKey: i18n.global.t("ticketDetails.ipAndCountry"),
    value: ips
      .map((location) => {
        const region = location.regionName ? `${escape(location.regionName)}, ` : "";
        return `<div>${escape(location.ip)} (${region}${escape(location.countryName)})</div>`;
      })
      .join(""),
  };
}

function mapModelAndOs(ticket: TicketDetails): TicketKeyDetail[] {
  return [
    {
      titleKey: i18n.global.t("ticketDetails.model"),
      value: ticket?.sections?.deviceMetaData.model,
    },
    {
      titleKey: i18n.global.t("ticketDetails.os"),
      value: ticket?.sections?.deviceMetaData.osVersionDisplayName,
    },
  ];
}

/**
 * Trailing device rows shared by every endpoint trigger: an IP/country row,
 * but only when the ticket carries locations (otherwise nothing is appended).
 */
function mapDeviceGenericDetails(ticket: TicketDetails): TicketKeyDetail[] {
  return ticket.sections.locations.length ? [mapIps(ticket.sections.locations)] : [];
}

function mapBlockedDestinationConnection(ticket: TicketDetails): TicketKeyDetail[] {
  const edrMetaData = ticket.sections.edrMetaData;
  const blockedConnectionMetaData = edrMetaData?.blockedConnectionMetaData;
  return [
    {
      titleKey: i18n.global.t("ticketDetails.processName"),
      value: escape(edrMetaData?.processName),
    },
    {
      titleKey: i18n.global.t("ticketDetails.processHash"),
      value: escape(edrMetaData?.processHash),
    },
    {
      titleKey: i18n.global.t("ticketDetails.pid"),
      value: escape(blockedConnectionMetaData?.pid),
    },
    {
      titleKey: i18n.global.t("ticketDetails.parentProcessName"),
      value: escape(blockedConnectionMetaData?.parentProcessName),
    },
    {
      titleKey: i18n.global.t("ticketDetails.parentPid"),
      value: escape(blockedConnectionMetaData?.parentPid),
    },
    {
      titleKey: i18n.global.t("ticketDetails.detectionTime"),
      value: blockedConnectionMetaData?.timestamp
        ? (getFormattedDateTime(blockedConnectionMetaData.timestamp, "ll") ?? "")
        : "",
    },
  ];
}

function mapFileIntegrityViolation(ticket: TicketDetails): TicketKeyDetail[] {
  const filesMetaData = ticket.sections.filesMetaData?.[0];
  return [
    { titleKey: i18n.global.t("ticketDetails.filePath"), value: escape(filesMetaData?.filePath) },
  ];
}
